import React, { useState, useCallback } from 'react';
import { TestResult, TestLogEntry, WaterBodyType } from '../types';
import { generateWeeklyDosingGuide, WeeklyDosingGuide, WeeklyDosingDay } from '../services/geminiService';
import { annotateUsageWithMetric } from '../utils/dosage';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  latestResults: TestResult | null;
  log: TestLogEntry[];
  poolVolume: number;
  waterBody: WaterBodyType;
  onSaveGuide?: (guide: WeeklyDosingGuide) => Promise<void>;
}

// ─── Day Card ─────────────────────────────────────────────────────────────────

const DayCard: React.FC<{ day: WeeklyDosingDay; dayIndex: number }> = ({ day, dayIndex }) => {
  const hasChemicals = day.chemicals && day.chemicals.length > 0;
  const hasTasks = day.tasks && day.tasks.length > 0;

  const dayColors = [
    'border-blue-500',
    'border-cyan-500',
    'border-teal-500',
    'border-green-500',
    'border-yellow-500',
    'border-orange-500',
    'border-purple-500',
  ];
  const color = dayColors[dayIndex % dayColors.length];

  return (
    <div className={`bg-slate-800 border-l-4 ${color} rounded-lg p-4 shadow-md`}>
      <h3 className="text-lg font-bold text-white mb-3">{day.day}</h3>

      {hasTasks && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">🔧 Maintenance</p>
          <ul className="space-y-1">
            {day.tasks.map((task, i) => (
              <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                <span className="text-slate-500 mt-0.5">•</span>
                <span>{task}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasChemicals && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">⚗️ Chemical Additions</p>
          <div className="space-y-2">
            {day.chemicals.map((chem, i) => (
              <div key={i} className="bg-slate-900/60 rounded-md p-2 text-sm">
                <p className="font-semibold text-cyan-300">{chem.name}</p>
                <p className="text-white font-bold">{annotateUsageWithMetric(chem.dose)}</p>
                <p className="text-slate-400 text-xs mt-0.5">{annotateUsageWithMetric(chem.instructions)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasChemicals && !hasTasks && (
        <p className="text-sm text-slate-500 italic">Rest day — no chemicals needed.</p>
      )}

      {day.notes && (
        <div className="mt-2 bg-yellow-900/30 border border-yellow-700/50 rounded p-2">
          <p className="text-xs text-yellow-300">💡 {day.notes}</p>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const WeeklyDosingTab: React.FC<Props> = ({ latestResults, log, poolVolume, waterBody, onSaveGuide }) => {
  const [guide, setGuide] = useState<WeeklyDosingGuide | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingGuide, setSavingGuide] = useState(false);
  const [savedGuide, setSavedGuide] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleGenerate = useCallback(async () => {
    if (!latestResults) return;
    setLoading(true);
    setError(null);
    setGuide(null);
    setSavedGuide(false);
    try {
      const result = await generateWeeklyDosingGuide(latestResults, poolVolume, waterBody, log);
      setGuide(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate weekly plan.');
    } finally {
      setLoading(false);
    }
  }, [latestResults, poolVolume, waterBody, log]);

  const handleSaveGuide = async () => {
    if (!guide || !onSaveGuide) return;
    setSavingGuide(true);
    setSavedGuide(false);
    setSaveError(null);
    try {
      await onSaveGuide(guide);
      setSavedGuide(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingGuide(false);
    }
  };

  if (!latestResults) {
    return (
      <div className="text-center text-slate-400 py-12 bg-slate-800 rounded-lg">
        <p className="text-4xl mb-3">📅</p>
        <p className="text-lg font-semibold">No test results yet</p>
        <p className="text-sm mt-1">Submit a water test first to generate your weekly dosing plan.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-slate-800 rounded-lg p-4 shadow-md">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-white">📅 Weekly Dosing Guide</h2>
            <p className="text-slate-400 text-sm mt-1">
              AI-generated 7-day chemical dosing &amp; maintenance plan based on your latest test results.
            </p>
          </div>
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold py-2 px-4 rounded-lg transition-colors text-sm flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating…
              </>
            ) : guide ? '🔄 Regenerate' : '✨ Generate Plan'}
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12 bg-slate-800 rounded-lg">
          <svg className="animate-spin h-10 w-10 text-cyan-400 mx-auto" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="mt-4 text-white font-semibold">Building your weekly plan…</p>
          <p className="text-slate-400 text-sm mt-1">Analyzing test results and scheduling chemical additions.</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="bg-red-900/50 border border-red-700 text-red-300 p-4 rounded-lg">
          <p className="font-bold">Error generating plan</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Guide content */}
      {guide && !loading && (
        <>
          {/* Summary + actions */}
          <div className="bg-slate-800 rounded-lg p-4 shadow-md">
            <div className="flex justify-between items-start flex-wrap gap-2 mb-3">
              <div>
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Week Starting</p>
                <p className="text-cyan-400 font-semibold">{guide.weekStarting}</p>
              </div>
              <div className="flex items-center gap-2">
                {onSaveGuide && (
                  <button
                    onClick={handleSaveGuide}
                    disabled={savingGuide}
                    className="bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white font-bold py-1.5 px-3 rounded-lg text-sm transition-colors"
                  >
                    📊 {savingGuide ? 'Saving…' : savedGuide ? '✅ Saved!' : 'Save to Pi'}
                  </button>
                )}
                <button
                  onClick={() => window.print()}
                  className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-1.5 px-3 rounded-lg text-sm transition-colors"
                >
                  🖨️ Print
                </button>
              </div>
            </div>

            <p className="text-slate-300 text-sm leading-relaxed">{guide.summary}</p>

            {saveError && <p className="mt-2 text-sm text-red-400">⚠️ {saveError}</p>}
          </div>

          {/* Alerts */}
          {guide.alertsAndWarnings && guide.alertsAndWarnings.length > 0 && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg p-4">
              <h3 className="font-bold text-red-300 mb-2">🚨 Alerts &amp; Warnings</h3>
              <ul className="space-y-1">
                {guide.alertsAndWarnings.map((alert, i) => (
                  <li key={i} className="text-sm text-red-200 flex items-start gap-2">
                    <span className="text-red-400 mt-0.5">▶</span>
                    <span>{alert}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Weekly goals */}
          {guide.weeklyGoals && guide.weeklyGoals.length > 0 && (
            <div className="bg-slate-800 rounded-lg p-4 shadow-md">
              <h3 className="font-bold text-white mb-2">🎯 This Week's Goals</h3>
              <ul className="space-y-1">
                {guide.weeklyGoals.map((goal, i) => (
                  <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-cyan-400 font-bold">{i + 1}.</span>
                    <span>{goal}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Daily plan */}
          <div className="space-y-3">
            {guide.days && guide.days.map((day, i) => (
              <DayCard key={i} day={day} dayIndex={i} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default WeeklyDosingTab;
